import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { VehicleType } from "@app/enum/VehicleType.enum";
import { ITriggerMetricsMap, ITriggerMetricsOverride, StreamaxCameraProfileService } from "@app/services/camera/streamax-camera-profile-service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { tap } from "rxjs";
import './custom-trigger-metrics.component.scss';

@UntilDestroy()
@Component({
    selector: `custom-trigger-metrics`,
    template: require('./custom-trigger-metrics.component.html')
})
export class CustomTriggerMetrics implements OnInit {
    @Input() profileId: number;
    @Input() isNew: boolean;
    @Input() triggerMetrics: ITriggerMetricsOverride[];
    @Input() overriddenVehicleTypes: VehicleType[];

    @Output() customTriggerUpdated = new EventEmitter<ITriggerMetricsOverride>();
    @Output() customTriggerDeleted = new EventEmitter<ITriggerMetricsOverride>();

    vehicleTypeForm: FormGroup;
    formCategories: { title: string, formGroup: FormGroup }[];

    VehicleTypes = VehicleType;
    vehicleTypes: number[];

    tableColumns: IVTableColumn<ITriggerMetricsOverride>[];
    tableRows: IVTableRow[];

    selectedRow: ITriggerMetricsOverride;
    showEditor: boolean = false;

    saveDisabled: boolean;

    constructor(
        private readonly vTableService: VTableService,
        private readonly formBuilder: FormBuilder,
        private readonly streamaxCameraProfileService: StreamaxCameraProfileService
    ) { }

    ngOnInit(): void {
        this.setupComponent();
    }

    onSaveCustomTrigger(): void {
        this.streamaxCameraProfileService.postTriggerOverride(this.profileId, this.selectedRow).pipe(untilDestroyed(this), tap(() => {

            const existingIndex = this.triggerMetrics.findIndex(
                (metric) => metric.VehicleType === this.selectedRow.VehicleType);

            if (existingIndex !== -1) {
                this.triggerMetrics[existingIndex] = this.selectedRow;
            } else {
                this.triggerMetrics = [...this.triggerMetrics, this.selectedRow];
            }

            this.overriddenVehicleTypes = [...this.overriddenVehicleTypes, this.selectedRow.VehicleType];

            this.vehicleTypes = Object.keys(VehicleType)
                .filter(s => !isNaN(Number(s)))
                .map(s => Number(s))
                .filter(v => !this.overriddenVehicleTypes?.includes(v));

            this.tableRows = this.vTableService.createTableRows(this.triggerMetrics, this.tableColumns);

            this.customTriggerUpdated.emit(this.selectedRow);

            this.closeCustomEditor();
        })).subscribe();
    }

    createNewOverride(): void {
        this.isNew = true;

        const firstAvailableEnumValue = this.vehicleTypes.find(type =>
            Object.values(VehicleType).includes(type as VehicleType)
        ) as VehicleType;

        this.selectedRow = {
            VehicleType: firstAvailableEnumValue,
            TriggerMetricsMap: {
                HarshAccelerationTriggerMetrics: {
                    Offset: 0.1,
                    EndSpeed: 200,
                    StartSpeed: 0,
                    ReAlertTimeSeconds: 10
                },
                HarshBrakingTriggerMetrics: {
                    Offset: 0.1,
                    EndSpeed: 200,
                    StartSpeed: 0,
                    ReAlertTimeSeconds: 10
                },
                LeftTurnTriggerMappings: {
                    Offset: 0.1,
                    EndSpeed: 200,
                    StartSpeed: 0,
                    ReAlertTimeSeconds: 10
                },
                RightTurnTriggerMappings: {
                    Offset: 0.1,
                    EndSpeed: 200,
                    StartSpeed: 0,
                    ReAlertTimeSeconds: 10
                },
                GyroTriggers: {
                    XTrigger: 1.6,
                    YTrigger: 1.8,
                    ZTrigger: 1.8
                }
            }
        }

        this.createFormGroups();

        this.showEditor = true;
    }

    private createVehicleTypeCategory(): void {
        this.vehicleTypeForm = new FormGroup({
            "VehicleType": new FormControl(this.selectedRow.VehicleType)
        });

        this.vehicleTypeForm.statusChanges.pipe(untilDestroyed(this))
            .subscribe(status => {
                this.UpdateTriggerMetrics(status);
            })
    }

    rowClicked($event): void {
        this.selectedRow = $event.data;
        this.createFormGroups();
        this.showEditor = true;
    }

    closeCustomEditor(): void {
        this.selectedRow = null;
        this.isNew = false;
        this.formCategories = [];
        this.showEditor = false;
    }

    private setupComponent(): void {
        this.createTable();

        this.vehicleTypes = Object.keys(VehicleType)
            .filter(s => !isNaN(Number(s)))
            .map(s => Number(s))
            .filter(v => !this.overriddenVehicleTypes?.includes(v));

        this.tableRows = this.vTableService.createTableRows(this.triggerMetrics, this.tableColumns);
    }

    deleteConfig($event: boolean): void {
        if (!$event) {
            return;
        }

        this.streamaxCameraProfileService.deleteTriggerOverride(this.profileId, this.selectedRow.VehicleType).pipe(untilDestroyed(this), tap(() => {

            this.triggerMetrics = this.triggerMetrics.filter(m => m.VehicleType != this.selectedRow.VehicleType);
                        
            this.overriddenVehicleTypes = this.overriddenVehicleTypes.filter(o => o != this.selectedRow.VehicleType);

            this.vehicleTypes = Object.keys(VehicleType)
                .filter(s => !isNaN(Number(s)))
                .map(s => Number(s))
                .filter(v => !this.overriddenVehicleTypes?.includes(v));

            this.tableRows = this.vTableService.createTableRows(this.triggerMetrics, this.tableColumns);

            this.customTriggerDeleted.emit(this.selectedRow);

            this.closeCustomEditor();
        })).subscribe();
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.VehicleType" }),
                createCellFunction: (override) => this.vTableService.createEnumCell(VehicleType, "VehicleType", override.VehicleType)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "StreamaxConfigs.HardAccOffset" }),
                createCellFunction: (override) => this.vTableService.createTableCell(override.TriggerMetricsMap.HarshAccelerationTriggerMetrics.Offset)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "StreamaxConfigs.HardAccSpeed" }),
                createCellFunction: (override) => this.vTableService.createTableCell(`${override.TriggerMetricsMap.HarshAccelerationTriggerMetrics.StartSpeed} - ${override.TriggerMetricsMap.HarshAccelerationTriggerMetrics.EndSpeed}`)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "StreamaxConfigs.HardBrakeOffset" }),
                createCellFunction: (override) => this.vTableService.createTableCell(override.TriggerMetricsMap.HarshBrakingTriggerMetrics.Offset)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "StreamaxConfigs.HardBrakeSpeed" }),
                createCellFunction: (override) => this.vTableService.createTableCell(`${override.TriggerMetricsMap.HarshBrakingTriggerMetrics.StartSpeed} - ${override.TriggerMetricsMap.HarshBrakingTriggerMetrics.EndSpeed}`)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "StreamaxConfigs.HardLeftOffset" }),
                createCellFunction: (override) => this.vTableService.createTableCell(override.TriggerMetricsMap.LeftTurnTriggerMappings.Offset)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "StreamaxConfigs.HardLeftSpeed" }),
                createCellFunction: (override) => this.vTableService.createTableCell(`${override.TriggerMetricsMap.LeftTurnTriggerMappings.StartSpeed} - ${override.TriggerMetricsMap.LeftTurnTriggerMappings.EndSpeed}`)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "StreamaxConfigs.HardRightOffset" }),
                createCellFunction: (override) => this.vTableService.createTableCell(override.TriggerMetricsMap.RightTurnTriggerMappings.Offset)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "StreamaxConfigs.HardRightSpeed" }),
                createCellFunction: (override) => this.vTableService.createTableCell(`${override.TriggerMetricsMap.RightTurnTriggerMappings.StartSpeed} - ${override.TriggerMetricsMap.RightTurnTriggerMappings.EndSpeed}`)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "StreamaxConfigs.XTrigger" }),
                createCellFunction: (override) => this.vTableService.createTableCell(override.TriggerMetricsMap.GyroTriggers.XTrigger)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "StreamaxConfigs.YTrigger" }),
                createCellFunction: (override) => this.vTableService.createTableCell(override.TriggerMetricsMap.GyroTriggers.YTrigger)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "StreamaxConfigs.ZTrigger" }),
                createCellFunction: (override) => this.vTableService.createTableCell(override.TriggerMetricsMap.GyroTriggers.ZTrigger)
            }
        ]
    }

    getControlType(control: FormControl): string {
        if (control.value instanceof Boolean || typeof control.value === 'boolean') {
            return 'boolean';
        } else if (typeof control.value === 'number') {
            return 'number';
        } else {
            return 'text';
        }
    }

    getStepValue(controlKey: string): number {
        switch (controlKey) {
            case "Offset":
                return 0.001
            case "XTrigger":
            case "YTrigger":
            case "ZTrigger":
                return 0.1
            default:
                return 1;
        }
    }

    getMaxValues(controlKey: string): number {
        switch (controlKey) {
            case "Offset":
                return 1;
            case "XTrigger":
            case "YTrigger":
            case "ZTrigger":
            case "ReAlertTimeSeconds":
                return 10;
            case "StartSpeed":
            case "EndSpeed":
                return 200;
            default:
                return 1;
        }
    }

    private createFormGroups(): void {
        this.formCategories = [];

        for (const category in this.selectedRow.TriggerMetricsMap) {
            let formCategory = {
                title: category,
                formGroup: this.createFormGroup(category)
            }

            formCategory.formGroup.statusChanges.pipe(untilDestroyed(this)).subscribe(status => {
                this.UpdateTriggerMetrics(status);
            })

            this.formCategories.push(formCategory);
        }

        this.createVehicleTypeCategory();
    }

    private createFormGroup(categoryName: string): FormGroup {
        const formGroupConfig = {};

        for (const key in this.selectedRow.TriggerMetricsMap[categoryName]) {

            if (this.selectedRow.TriggerMetricsMap[categoryName].hasOwnProperty(key)) {
                formGroupConfig[key] = new FormControl(this.selectedRow.TriggerMetricsMap[categoryName][key], [Validators.required, Validators.min(0), Validators.max(this.getMaxValues(key))]);
            }
        }

        return this.formBuilder.group(formGroupConfig);
    }

    private UpdateTriggerMetrics(status: string): void {
        if (status === "INVALID") {
            this.saveDisabled = true;
            return;
        }

        this.saveDisabled = false;

        const updatedTriggerMetricsMap = {};

        this.formCategories.forEach(category => {
            updatedTriggerMetricsMap[category.title] = category.formGroup.getRawValue();
        });

        this.selectedRow = {
            ...this.selectedRow,
            VehicleType: this.vehicleTypeForm.get("VehicleType").value,
            TriggerMetricsMap: updatedTriggerMetricsMap as ITriggerMetricsMap,
        };
    }
}
